"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import type LenisType from "lenis";
import { setLenisInstance, setScrolling } from "@/lib/lenis-instance";

let scrollTriggerMod: typeof import("gsap/ScrollTrigger")["ScrollTrigger"] | null = null;

export function SmoothScrollProvider({ children }: { children: React.ReactNode }) {
  const lenisRef = useRef<LenisType | null>(null);
  const pathname = usePathname();

  const isFirstPathnameRun = useRef(true);
  useEffect(() => {
    if (isFirstPathnameRun.current) {
      isFirstPathnameRun.current = false;
      return;
    }
    window.scrollTo(0, 0);
    // ScrollTrigger may not have loaded yet on a very fast route change before setup() ran; once it
    // has, refresh the new page's pin positions against the fresh DOM.
    const raf = requestAnimationFrame(() => scrollTriggerMod?.refresh());
    const t = setTimeout(() => scrollTriggerMod?.refresh(), 300);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [pathname]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    const setup = async () => {
      if (cancelled) return;

      const [{ default: gsap }, { ScrollTrigger }, { default: Lenis }] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
        import("lenis"),
      ]);
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);
      // Publish onto the shared handle so the route-change refresh effect can reach it.
      scrollTriggerMod = ScrollTrigger;

      // Don't let a mobile browser's URL-bar show/hide fire a full refresh (which re-pins
      // mid-scroll and reads as a "jump backwards"). Also anchor pins to the top so their start
      // never drifts.
      ScrollTrigger.config({ ignoreMobileResize: true });

      const lenis = new Lenis({
        duration: 1.05,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        // key to a smooth, conflict-free hand-off: Lenis owns wheel/touch, GSAP reads its scroll.
        syncTouch: false,
      });
      lenisRef.current = lenis;
      setLenisInstance(lenis);

      // Single source of truth: Lenis drives on GSAP's ticker, and every scroll tick updates
      // ScrollTrigger. No second rAF loop, so scrub timelines never fight a competing frame clock.
      // Lenis scrolls the window natively (no wrapper/content element), so ScrollTrigger reads the
      // real window scroll directly — no scrollerProxy. The previous proxy routed ScrollTrigger's
      // scroll WRITES through lenis.scrollTo(staleValue), which yanked the page backwards to a stale
      // cached limit at the bottom and could lock scrolling. Driving Lenis on the ticker and calling
      // ScrollTrigger.update on each scroll tick is all that's needed to keep scrub in sync.
      lenis.on("scroll", ScrollTrigger.update);
      const rafCallback = (time: number) => lenis.raf(time * 1000);
      gsap.ticker.add(rafCallback);
      gsap.ticker.lagSmoothing(0);

      // Track whether the user is actively scrolling. A refresh (especially the deep revert/re-apply
      // pass) recalculates scrub triggers mid-gesture and tugs the scroll position backwards, which
      // reads as a freeze near the bottom. Expose the flag so refreshes can wait for scroll-idle.
      let scrolling = false;
      let idleTimer: ReturnType<typeof setTimeout>;
      lenis.on("scroll", () => {
        scrolling = true;
        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          scrolling = false;
          if (pendingRefresh) {
            pendingRefresh = false;
            lenis.resize();
            ScrollTrigger.refresh();
          }
        }, 150);
      });
      setScrolling(() => scrolling);

      let pendingRefresh = false;
      const refreshAll = () => {
        if (scrolling) {
          pendingRefresh = true;
          return;
        }
        lenis.resize();
        ScrollTrigger.refresh();
      };

      let refreshTimeout: ReturnType<typeof setTimeout>;
      const debouncedRefresh = () => {
        clearTimeout(refreshTimeout);
        refreshTimeout = setTimeout(refreshAll, 250);
      };
      let lastWidth = window.innerWidth;
      const onResize = () => {
        // width-only resizes matter for layout; height-only (mobile URL bar) does not
        if (window.innerWidth !== lastWidth) {
          lastWidth = window.innerWidth;
          debouncedRefresh();
        }
      };
      window.addEventListener("resize", onResize);

      // Keep Lenis's cached scroll limit in sync with the real page height. Images, the sticky
      // sidebar, and lazy content grow the page AFTER Lenis initialises; without this, wheel/
      // trackpad scroll clamps short of the footer (a scrollbar drag still reaches it — the tell).
      // lenis.resize() is cheap and safe mid-scroll (unlike ScrollTrigger.refresh(), which tugs
      // the position), so this observer only resizes Lenis and never refreshes.
      let lastHeight = document.body.scrollHeight;
      const heightObserver = new ResizeObserver(() => {
        const h = document.body.scrollHeight;
        if (h !== lastHeight) {
          lastHeight = h;
          lenis.resize();
        }
      });
      heightObserver.observe(document.body);

      // one refresh after everything (fonts, lazy canvases) has settled — not on a live observer
      window.addEventListener("load", refreshAll);
      document.fonts?.ready?.then(refreshAll);
      const hasPinnedTriggers = ScrollTrigger.getAll().some((t) => t.pin);
      const t1 = hasPinnedTriggers ? setTimeout(refreshAll, 600) : undefined;
      const t2 = hasPinnedTriggers ? setTimeout(refreshAll, 1600) : undefined;
      const t3 = hasPinnedTriggers ? setTimeout(refreshAll, 3000) : undefined;

      const handleAnchorClick = (e: MouseEvent) => {
        const target = (e.target as HTMLElement).closest('a[href^="#"]');
        if (!target) return;
        const href = target.getAttribute("href");
        if (!href || href === "#") return;
        const el = document.querySelector(href);
        if (!el) return;
        e.preventDefault();
        lenis.scrollTo(el as HTMLElement, { offset: -80, duration: 1.4 });
        history.replaceState(null, "", href);
      };
      document.addEventListener("click", handleAnchorClick);

      cleanup = () => {
        document.removeEventListener("click", handleAnchorClick);
        window.removeEventListener("resize", onResize);
        heightObserver.disconnect();
        clearTimeout(idleTimer);
        clearTimeout(refreshTimeout);
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        gsap.ticker.remove(rafCallback);
        lenis.destroy();
        lenisRef.current = null;
        setLenisInstance(null);
      };
    };

    const hasIdleCallback = typeof window.requestIdleCallback === "function";
    const idleId = hasIdleCallback ? window.requestIdleCallback(setup) : window.setTimeout(setup, 100);

    return () => {
      cancelled = true;
      if (hasIdleCallback) window.cancelIdleCallback(idleId as number);
      else window.clearTimeout(idleId as number);
      cleanup?.();
    };
  }, []);

  return <>{children}</>;
}
